import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type ToastVariant = "success" | "error" | "info" | "warning";

type Toast = {
  id: number;
  variant: ToastVariant;
  message: string;
};

type ToastContextValue = {
  show: (variant: ToastVariant, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const variantStyles: Record<ToastVariant, { bg: string; border: string; icon: string; text: string }> = {
  success: { bg: "bg-emerald-950/90", border: "border-emerald-500/40", icon: "✓", text: "text-emerald-100" },
  error:   { bg: "bg-red-950/90",     border: "border-red-500/40",     icon: "✕", text: "text-red-100" },
  info:    { bg: "bg-blue-950/90",    border: "border-blue-500/40",    icon: "ℹ", text: "text-blue-100" },
  warning: { bg: "bg-amber-950/90",   border: "border-amber-500/40",   icon: "⚠", text: "text-amber-100" },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (variant: ToastVariant, message: string) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, variant, message }]);
      setTimeout(() => dismiss(id), 4500);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m) => show("success", m),
      error: (m) => show("error", m),
      info: (m) => show("info", m),
      warning: (m) => show("warning", m),
    }),
    [show],
  );

  // El aviso va en su PROPIO portal a <body> y por encima de todo lo demás.
  //
  // Antes vivía dentro de #root con z-[100]: los modales se montan con portal en
  // <body> (después de #root) y varios usan z-[100] también, así que con el
  // mismo z-index ganaba el último del DOM y el aviso quedaba TAPADO por el
  // fondo negro del modal. La acción salía bien y el usuario no veía nada —
  // exactamente el caso de "Copiar mensaje" en Invitar equipo. El overlay más
  // alto de la casa hoy es z-[121] (StyledSelect); 1000 deja margen.
  //
  // Ancho: en el teléfono se ancla a los dos bordes (con right-4 + w-full el
  // aviso medía 24rem y se salía por la izquierda en pantallas de 375px).
  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="fixed top-4 left-4 right-4 z-[1000] flex flex-col gap-2 pointer-events-none sm:left-auto sm:w-full sm:max-w-sm">
          {toasts.map((t) => {
            const v = variantStyles[t.variant];
            return (
              <div
                key={t.id}
                role="status"
                // data-surface="dark": el aviso es oscuro también en modo claro
                // (es un snackbar). Sin esto, el override global de claro pinta
                // el texto/✕ de gris oscuro sobre fondo casi negro.
                data-surface="dark"
                className={`pointer-events-auto rounded-lg border ${v.bg} ${v.border} ${v.text} backdrop-blur-sm shadow-card px-4 py-3 flex items-start gap-3 animate-slide-up`}
              >
                <span aria-hidden className="text-base leading-none mt-0.5">
                  {v.icon}
                </span>
                <span className="flex-1 text-sm">{t.message}</span>
                <button
                  onClick={() => dismiss(t.id)}
                  className="text-white/40 hover:text-white -mr-1"
                  aria-label="dismiss"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast debe usarse dentro de <ToastProvider>");
  return ctx;
}
